import { ref, computed, watch } from '@vue/composition-api'

import debounce from 'lodash/debounce'

import useRequest from '@/compositions/useRequest'

import OrderCreate from '@/components/@dispatcher/OrderCreate'
import CalendarFilter from '@/components/@dispatcher/CalendarFilter'
import GanttChart from '@/components/GanttChart'

import { getOccupationType } from '@/constants/occupations'

import createEvent from '@/models/calendar/event'
import OccupationDialog from './components/OccupationDialog'
import { getInstanceIconUrl } from '@/utils/instanceGetters'
import { backendFormat, currentMonthPeriod } from '@/utils/formatDate'

const translateFilterData = (data) => {
  const translationFields = {
    kindId: 'kindIdEq',
    companyId: 'companyIdEq',
  }

  Object
    .keys(data)
    .forEach((key) => {
      if (translationFields[key]) {
        data[translationFields[key]] = data[key]
        delete data[key]
      }
    })

  const locations = ['in_location_position', 'in_district']

  locations.forEach((key) => {
    if (!data[key] || parseInt(data[key], 0) === 0) {
      delete data[key]
    } else {
      data[key] = parseInt(data[key], 0) * 1000
    }
  })

  const properties = ['property_1_id', 'property_2_id']

  data.values_id_in_array = []
  properties.forEach((key) => {
    if (data[key]) data.values_id_in_array.push(data[key])
    delete data[key]
  })

  if (data.address && data.address.position) {
    data.lat = data.address.position.lat
    data.lon = data.address.position.lon
  }
  data.state_eq = 'active'

  delete data.address

  return data
}

const calendarStyles = {
  solidBorder: true,
  titleBorder: true,
  fullHeightTitle: true,
}

export default {
  name: 'PersonalCalendar',
  props: {
    hideDetails: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    OrderCreate, GanttChart, CalendarFilter, OccupationDialog,
  },
  data() {
    return {
      isExpanded: true,
    }
  },
  methods: {
    changeStyles() {
      this.isExpanded = !this.isExpanded
    },
  },
  computed: {
    mediaStyles() {
      return this.isExpanded
        ? {
          imageWrap: this.$style.imageWrap,
          image: this.$style.image,
          headWrapper: this.$style.headWrapper,
          title: this.$style.title,
          hours: this.$style.hours,
          instanceData: this.$style.instanceData,
        }
        : {
          imageWrap: this.$style.wrappedImageWrap,
          image: this.$style.wrappedImage,
          headWrapper: this.$style.wrappedHeadWrapper,
          title: this.$style.wrappedTitle,
          hours: this.$style.wrappedHours,
          instanceData: this.$style.wrappedInstanceData,
        }
    },
  },
  setup(_, context) {
    const { $store } = context.root

    const diagramRef = ref(null)
    const occupationData = ref(null)
    const occupationVisible = ref(false)
    const orderCreateVisible = ref(false)
    const occupationFromBtnOpen = ref(false)

    const order = ref({})
    const items = ref([])
    const selectedInstances = ref([])
    const period = ref({ start_date: currentMonthPeriod.start, end_date: currentMonthPeriod.end })

    const userRole = computed(() => $store.getters['auth/userRole'])
    const wialonConnection = computed(() => $store.state.auth.wialonConnection)

    const { makeRequest, loading } = useRequest({
      context,
      request: data => $store.dispatch('calendar/fetchAllInstances', data),
    })

    const isDone = state => state === 'done'

    const getName = hours => `${hours} ч.`
    const getEventTitle = (item) => {
      if (item.type === 'Tracker') return `Трек №${item.id}`
      if (isDone(item.state)) {
        return `Завершенный заказ №${item.id}`
      }
      if (item.state === 'cancelled') {
        return `Отмененный заказ №${item.id}`
      }
      return `Активный заказ №${item.id}`
    }

    const prepareItem = (item) => {
      const orders = item.works.map(obj => createEvent({
        name: getEventTitle(obj),
        color: 'green lighten-5',
        pinColor: isDone(obj.state) || obj.state === 'cancelled' ? 'grey' : 'green',
        order: obj,
        ...obj,
      }))

      const ordersRequest = item.ordersRequest.map(obj => createEvent({
        name: `Заявка №${obj.id}`,
        color: 'yellow lighten-5',
        pinColor: 'yellow',
        order: obj,
        ...obj,
      }))

      let occupations = []
      occupations = item.occupations.map(obj => createEvent({
        id: obj.id,
        type: 'occupation',
        name: `Техника занята: ${getOccupationType(obj.occupationType).label} ${(obj?.comment.length > 0 && userRole.value !== 'user') ? `(${obj.comment})` : ''}`,
        color: 'grey darken-5',
        pinColor: 'grey',
        ...obj,
      }))

      const spent = item.groupedTime?.map(obj => createEvent({
        name: getName(obj.hours),
        color: 'blue lighten-5',
        pinColor: '',
        order: {
          ...obj,
          orderId: obj.orderId,
          type: 'Instance',
          instanceId: item.id,
        },
        dateStart: obj.day,
        dateEnd: obj.day,
        ...obj,
      }))

      return {
        events: [...spent, ...orders, ...ordersRequest, ...occupations],
        id: item.id,
        name: item.name,
        image: getInstanceIconUrl(item.iconUrl),
        spendedTime: item.spendedTimeCount,
        raw: item,
      }
    }
    const fetchData = async (data = {}) => {
      Object.assign(data, period.value)
      const { instances } = await makeRequest(translateFilterData(data))
      items.value = instances?.map(prepareItem)
      selectedInstances.value = []
    }

    const debouncedFetch = debounce(fetchData, 1000, {
      leading: false,
      trailing: true,
    })

    const handleFilterUpdate = data => debouncedFetch(data)

    fetchData()

    const selectAll = computed({
      get: () => selectedInstances.value.length === items.length,
      set: (value) => {
        if (value) {
          selectedInstances.value = items.value.map(({ raw }) => raw.id)
        } else {
          selectedInstances.value = []
        }
      },
    })

    const updateFromChart = async (params) => {
      if (params) {
        period.value = { start_date: backendFormat(new Date(params.from)), end_date: backendFormat(new Date(params.to)) }
      }
      await fetchData()
    }

    watch(() => occupationVisible.value, (value) => {
      if (value) return
      if (occupationFromBtnOpen.value) {
        occupationFromBtnOpen.value = false
        return
      }
      diagramRef.value.clearSelection()
    })

    const showOccupationDialog = (data) => {
      occupationVisible.value = true
      occupationData.value = data
    }

    const addOccupationClick = () => {
      showOccupationDialog()
      occupationFromBtnOpen.value = true
    }

    const selectedInstancesModels = computed(() => selectedInstances.value.map(instance => items.value.find(item => item.raw.id === instance)))

    const modelNumber = computed(() => {
      const itemsName = items.value.map(el => el.name.split(' '))
      const newItemsName = itemsName.map(item => item.filter(el => el !== ''))
      const machineNumber = newItemsName.map(el => el.slice(3, 6).join(' '))
      const machineNumberList = items.value.map((el, index) => ({
        id: el.id,
        number:
        machineNumber[index],
      }))
      return machineNumberList
    })

    return {
      items,
      modelNumber,
      loading,
      order,
      selectAll,
      selectedInstances,
      selectedInstancesModels,
      wialonConnection,
      diagramRef,
      occupationData,
      occupationVisible,
      orderCreateVisible,
      calendarStyles,
      userRole,
      showOccupationDialog,
      addOccupationClick,
      fetchData,
      updateFromChart,
      handleFilterUpdate,
      submitCreate: () => { orderCreateVisible.value = true },
    }
  },
}
